import axios from 'axios';

class AzureReminderService {
    constructor() {
        // Base URL is read from the environment variable, which is set differently
        // in local development (.env.local) and on Netlify.
        this.baseUrl = process.env.REACT_APP_AZURE_FUNCTIONS_URL;
            
        this.axiosInstance = axios.create({
            baseURL: this.baseUrl,
            timeout: 30000,
            headers: {
                'Content-Type': 'application/json',
            },
        });
        this.pollingInterval = null;
        this.deviceToken = this.generateDeviceToken();
    }

    generateDeviceToken() {
        // Try to get existing device token from localStorage
        let token = localStorage.getItem('azure_device_token');
        if (!token) {
            token = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            localStorage.setItem('azure_device_token', token);
        }
        return token;
    }

    async createReminder(reminderData) {
        try {
            const { name: clientName, date, time } = reminderData;
            
            const meetingDateTime = new Date(`${date}T${time}`);
            
            const payload = {
                clientName,
                meetingDate: meetingDateTime.toISOString(),
                deviceToken: this.deviceToken
            };
            
            console.log('📤 Sending reminder to Azure:', payload);
            
            const response = await this.axiosInstance.post('/CreateReminder', payload);
            
            console.log('✅ Azure response:', response.data);
            
            return {
                success: true,
                data: response.data,
                message: response.data.message || 'Reminder created successfully',
                meetingDetails: response.data.meetingDetails
            };
            
        } catch (error) {
            console.error('❌ Error creating reminder:', error);
            
            let errorMessage = 'Failed to create reminder';
            if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            return {
                success: false,
                error: errorMessage
            };
        }
    }

    async getChatNotifications(deviceToken = null) {
        try {
            const token = deviceToken || this.deviceToken;
            
            const response = await this.axiosInstance.post('/GetChatNotifications', {
                deviceToken: token
            });
            
            console.log('📨 Notification response:', response.data);
            
            return {
                success: true,
                notifications: response.data.notifications || [],
                count: response.data.count || 0
            };
        } catch (error) {
            console.error('❌ Error getting chat notifications:', error);
            return {
                success: false,
                error: error.message || 'Failed to get notifications',
                notifications: []
            };
        }
    }

    startNotificationPolling(onNotification, intervalMs = 20000) {
        this.stopNotificationPolling();
        
        console.log('🔄 Starting notification polling every', intervalMs / 1000, 'seconds');
        console.log('📱 Polling with device token:', this.deviceToken);
        
        // Poll immediately once
        this.pollOnce(onNotification);
        
        // Then set up interval
        this.pollingInterval = setInterval(() => {
            this.pollOnce(onNotification);
        }, intervalMs);
    }

    async pollOnce(onNotification) {
        try {
            const result = await this.getChatNotifications();
            if (result.success && result.notifications.length > 0) {
                console.log('📨 Received', result.notifications.length, 'new notifications');
                result.notifications.forEach(notification => {
                    console.log('🔔 Processing notification:', notification);
                    onNotification(notification);
                });
            } else {
                console.log('📭 No new notifications');
            }
        } catch (error) {
            console.error('❌ Polling error:', error);
        }
    }

    stopNotificationPolling() {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
            console.log('⏹️ Stopped notification polling');
        }
    }

    async testConnection() {
        try {
            const response = await this.axiosInstance.get('/TestConnection');
            console.log('✅ Connection test passed:', response.data);
            return { success: true, data: response.data };
        } catch (error) {
            console.error('❌ Connection test failed:', error);
            return { success: false, error: error.message };
        }
    }

    // Manual test method for creating a test reminder
    async createTestReminder() {
        const testData = {
            name: "Test Client",
            date: new Date().toISOString().split('T')[0], // Today
            time: new Date(Date.now() + 5 * 60 * 1000).toTimeString().slice(0, 5) // 5 minutes from now
        };
        
        console.log('🧪 Creating test reminder:', testData);
        return await this.createReminder(testData);
    }
}

export default new AzureReminderService();